import _ from 'lodash';
import React, { useState, useRef, useEffect } from 'react';
import { GridBag, BodyText, Button, Div, Gutter, Headline, Dropdown, Divider, Loader, Accordion } from '@gaiads/telia-react-component-library';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Prompt, useHistory } from 'react-router-dom';
import { AdminProductTypeEnum } from '@cdaas/api-clients/dist/product-service-openapi';

import { mapBackendErrorCodeToLocalizedErrorMsg, resolveAxiosErrorResponse } from '../../../utils/errors';
import { ProductEditProps, Errors, validate, createBackendProduct, ensureEditableItemInputChangesSavedOnSaveBtnClick } from './utils';
import BasicDataSection from './BasicData/BasicData';
import GroupNameSection from './GroupName/GroupName';
import VariantsDetailSection from './VariantsDetail/VariantsDetail';
import DiscoverabilitySection from './Discoverability/Discoverability';
import ImagesSection from './Images/Images';
import KeySellingPointsSection from './KeySellingPoints/KeySellingPoints';
import LongDescriptionSection from './LongDescription/LongDescription';
import SpecsSection from './Specs/Specs';
import BoxContentsSection from './BoxContents/BoxContents';
import VideosSection from './Videos/Videos';
import { setNotification } from '../../../store/ui';
import Sections, { SectionsProps } from '../../../shared/Sections/Sections';
import { productServiceAdminProductsClient } from '../../../api-client/product-service-client';
import FinanceProductIds from './FinanceProductId/FinanceProductIds';

import './ProductEdit.scss';

const saveProductButtonId = 'save-product-btn';

export function ProductEdit(props: ProductEditProps) {
  const {
    id,
    type,
    handleTypeChange,
    brand,
    handleBrandChange,
    name,
    handleNameChange,
    nameTranslations,
    handleNameTranslationsChange,
    handleVariantsTeliaFinanceGUIDChange,
    variants,
    handleVariantsChange,
    subCategories,
    handleSubCategoriesChange,
    tags,
    handleTagsChange,
    images,
    handleImagesChange,
    translationsMetadata,
    keySellingPoints,
    handleKeySellingPointsChange,
    keySellingPointsTranslations,
    handleKeySellingPointsTranslationsChange,
    description,
    handleDescriptionChange,
    descriptionTranslations,
    handleDescriptionTranslationsChange,
    boxContents,
    handleBoxContentsChange,
    boxContentsTranslations,
    handleBoxContentsTranslationsChange,
    specs,
    handleSpecsChange,
    videos,
    handleVideosChange,
    properties,
    handleAddProperty,
    handleRemoveProperty,
    allTagsInSystem,
    allPropertiesInSystem,
    allCategoriesInSystem,
    currentLocale,
    locales,
    handleLocaleChange,
    variationsExist,
    addMode,
    countries,
  } = props;

  const { t } = useTranslation('productscreen');
  const history = useHistory();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Errors>();

  // For checking if user has made changes and tries to navigate away
  const productWithoutAnyChangesRef = useRef(
    createBackendProduct({
      id,
      type,
      brand,
      name,
      nameTranslations,
      variants,
      subCategories,
      tags,
      images,
      translationsMetadata,
      keySellingPoints,
      keySellingPointsTranslations,
      description,
      descriptionTranslations,
      boxContents,
      boxContentsTranslations,
      specs,
      videos,
      properties,
    }),
  );

  const [saveProductChanges, setSaveProductChanges] = useState<boolean>(false);

  const handleSubmit = async (): Promise<boolean> => {
    const { valid, errors } = validate({
      name,
      brand,
      variants,
      subCategories,
      keySellingPoints,
      description,
      descriptionTranslations,
      boxContents,
      specs,
      videos,
      properties,
      allPropertiesInSystem,
      locales,
      t,
    });

    if (!valid) {
      setErrors(errors);
      dispatch(
        setNotification({
          text: t('editscreen.errors.validation'),
          show: true,
          type: 'warning',
        }),
      );
    } else {
      setErrors(undefined);
      setLoading(true);
      const product = createBackendProduct({
        id,
        type,
        brand,
        name,
        nameTranslations,
        variants,
        subCategories,
        tags,
        images,
        translationsMetadata,
        keySellingPoints,
        keySellingPointsTranslations,
        description,
        descriptionTranslations,
        boxContents,
        boxContentsTranslations,
        specs,
        videos,
        properties,
      });
      try {
        if (addMode) {
          await productServiceAdminProductsClient.postAdminProduct(product);
        } else if (id) {
          await productServiceAdminProductsClient.putAdminProduct(id, product);
        }
        history.push('/');
        return true;
      } catch (error) {
        setLoading(false);
        dispatch(
          setNotification({
            text: mapBackendErrorCodeToLocalizedErrorMsg(
              resolveAxiosErrorResponse(error)?.data,
              addMode ? t('editscreen.errors.create_product') : t('editscreen.errors.update_product'),
            ),
            show: true,
            type: 'warning',
          }),
        );
      }
    }
    return false;
  };

  useEffect(() => {
    const submit = async () => {
      const success = await handleSubmit();
      if (!success) {
        setSaveProductChanges(false);
      }
    };
    if (saveProductChanges) {
      submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveProductChanges]);

  const sectionTitles: SectionsProps['sectionTitles'] = [];
  if (!variationsExist) {
    sectionTitles.push({
      id: 'basic-data',
      title: t('editscreen.product_section_titles_without_variants.basic_data'),
      sectionHasErrors: Boolean(errors?.name || errors?.sku || errors?.brand),
    });
  } else {
    sectionTitles.push({
      id: 'group-name',
      title: t('editscreen.product_section_titles_with_variants.group_name'),
      sectionHasErrors: Boolean(errors?.name || errors?.brand),
    });
  }
  if (type === AdminProductTypeEnum.Hardware) {
    sectionTitles.push({
      id: 'tf-product-ids',
      title: t('editscreen.product_section_titles_with_variants.tfProductId'),
      sectionHasErrors: Boolean(errors?.variants),
    });
  }
  if (variationsExist) {
    sectionTitles.push({
      id: 'variants-detail',
      title: t('editscreen.product_section_titles_with_variants.variants_detail'),
      sectionHasErrors: Boolean(errors?.variants),
    });
  }
  sectionTitles.push(
    {
      id: 'discoverability',
      title: t('editscreen.common_product_section_titles.discoverability'),
      sectionHasErrors: Boolean(errors?.category),
    },
    {
      id: 'images',
      title: t('editscreen.common_product_section_titles.images'),
      sectionHasErrors: false,
    },
    {
      id: 'key-selling-points',
      title: t('editscreen.common_product_section_titles.key_selling_points'),
      sectionHasErrors: Boolean(errors?.keySellingPoints),
    },
    {
      id: 'long-description',
      title: t('editscreen.common_product_section_titles.long_description'),
      sectionHasErrors: Boolean(errors?.description),
    },
    {
      id: 'specs',
      title: t('editscreen.common_product_section_titles.specs'),
      sectionHasErrors: Boolean(errors?.specs),
    },
    {
      id: 'box-contents',
      title: t('editscreen.common_product_section_titles.box_contents'),
      sectionHasErrors: Boolean(errors?.boxContents),
    },
    {
      id: 'videos',
      title: t('editscreen.common_product_section_titles.videos'),
      sectionHasErrors: Boolean(errors?.videos),
    },
  );

  const sectionsRef = useRef<(HTMLDivElement | null)[]>(new Array(Object.keys(sectionTitles).length));

  const renderSectionContent = (title: string) => {
    switch (title) {
      case t('editscreen.product_section_titles_without_variants.basic_data'):
        return (
          <BasicDataSection
            title={title}
            type={type}
            sku={variants?.[0]?.sku}
            name={name}
            brand={brand}
            locale={currentLocale}
            nameTranslations={nameTranslations}
            handleTypeChange={handleTypeChange}
            handleNameChange={handleNameChange}
            handleNameTranslationsChange={handleNameTranslationsChange}
            handleBrandChange={handleBrandChange}
            nameError={errors?.name}
            skuError={errors?.sku}
            brandError={errors?.brand}
          />
        );
      case t('editscreen.product_section_titles_with_variants.group_name'):
        return (
          <GroupNameSection
            title={title}
            type={type}
            name={name}
            brand={brand}
            locale={currentLocale}
            nameTranslations={nameTranslations}
            handleTypeChange={handleTypeChange}
            handleNameChange={handleNameChange}
            handleNameTranslationsChange={handleNameTranslationsChange}
            handleBrandChange={handleBrandChange}
            nameError={errors?.name}
            brandError={errors?.brand}
          />
        );
      case t('editscreen.product_section_titles_with_variants.tfProductId'):
        return (
          <FinanceProductIds
            financeIdsPerCountry={(variants?.[0].finance_product_ids || []).reduce(
              (prev, curr) => ({
                ...prev,
                [curr.country_id.toString()]: curr.finance_product_id,
              }),
              {},
            )}
            handleVariantsTeliaFinanceGUIDChange={handleVariantsTeliaFinanceGUIDChange}
            countries={countries}
            errors={errors?.financeId}
          />
        );
      case t('editscreen.product_section_titles_with_variants.variants_detail'):
        return (
          <VariantsDetailSection
            title={title}
            allPropertiesInSystem={allPropertiesInSystem}
            variants={variants}
            properties={properties}
            handleVariantsChange={handleVariantsChange}
            handleAddProperty={handleAddProperty}
            handleRemoveProperty={handleRemoveProperty}
            brand={brand}
            images={images}
            handleImagesChange={handleImagesChange}
            errors={errors?.variants}
          />
        );
      case t('editscreen.common_product_section_titles.discoverability'):
        return (
          <DiscoverabilitySection
            title={title}
            productGategoryIds={subCategories}
            productTagIds={tags}
            allCategoriesOfSystem={allCategoriesInSystem}
            allTagsOfSystem={allTagsInSystem}
            handleSubCategoriesChange={handleSubCategoriesChange}
            handleTagsChange={handleTagsChange}
            categoryError={errors?.category}
          />
        );
      case t('editscreen.common_product_section_titles.images'):
        return (
          <ImagesSection
            title={title}
            images={images}
            variationsExist={variationsExist}
            variants={variants}
            allPropertiesInSystem={allPropertiesInSystem}
            properties={properties}
            handleImagesChange={handleImagesChange}
          />
        );
      case t('editscreen.common_product_section_titles.key_selling_points'):
        return (
          <KeySellingPointsSection
            title={title}
            keySellingPoints={keySellingPoints}
            keySellingPointsTranslations={keySellingPointsTranslations}
            locale={currentLocale}
            handleKeySellingPointsChange={handleKeySellingPointsChange}
            handleKeySellingPointsTranslationsChange={handleKeySellingPointsTranslationsChange}
            errors={errors?.keySellingPoints}
          />
        );
      case t('editscreen.common_product_section_titles.long_description'):
        return (
          <LongDescriptionSection
            title={title}
            description={description}
            descriptionTranslations={descriptionTranslations}
            locale={currentLocale}
            handleDescriptionChange={handleDescriptionChange}
            handleDescriptionTranslationsChange={handleDescriptionTranslationsChange}
            errors={errors?.description}
          />
        );
      case t('editscreen.common_product_section_titles.specs'):
        return (
          <SpecsSection
            title={title}
            specs={specs}
            currentLocale={currentLocale}
            locales={locales}
            handleSpecsChange={handleSpecsChange}
            specsErrors={errors?.specs}
          />
        );
      case t('editscreen.common_product_section_titles.box_contents'):
        return (
          <BoxContentsSection
            title={title}
            boxContents={boxContents}
            boxContentsTranslations={boxContentsTranslations}
            locale={currentLocale}
            handleBoxContentsChange={handleBoxContentsChange}
            handleBoxContentsTranslationsChange={handleBoxContentsTranslationsChange}
            errors={errors?.boxContents}
          />
        );
      case t('editscreen.common_product_section_titles.videos'):
        return (
          <VideosSection title={title} videos={videos} locale={currentLocale} handleVideosChange={handleVideosChange} videosError={errors?.videos} />
        );
    }
  };

  return (
    <Div>
      <Prompt
        message={() => {
          // Navigation is caused by user clicking the save changes -button
          if (saveProductChanges) {
            return true;
          }
          const productWithPossibleChanges = createBackendProduct({
            id,
            type,
            brand,
            name,
            nameTranslations,
            variants,
            subCategories,
            tags,
            images,
            translationsMetadata,
            keySellingPoints,
            keySellingPointsTranslations,
            description,
            descriptionTranslations,
            boxContents,
            boxContentsTranslations,
            specs,
            videos,
            properties,
          });
          const unsavedChanges = !_.isEqual(productWithPossibleChanges, productWithoutAnyChangesRef.current);
          return unsavedChanges ? t('editscreen.confirm_unsaved_changes') : true;
        }}
      />
      {loading && (
        <Div className="product-edit-loading-overlay" alignTextToCenter padding={{ top: 'xlg' }}>
          <Loader size="lg" />
        </Div>
      )}
      <GridBag>
        {/* Left-side container */}
        <GridBag.Item md={9}>
          <BodyText>{addMode ? t('editscreen.add_product_title') : t('editscreen.update_product_title')}</BodyText>
          <Headline size="lg">{name}</Headline>
          <Gutter size="xlg" />
          <Div padding={{ size: 'md' }} boxShadow="sm">
            {sectionTitles.map(({ id, title }, index) => (
              <Div key={title}>
                <div id={id} ref={el => (sectionsRef.current[index] = el)} /*for navigating to section from a section title*/></div>
                {renderSectionContent(title)}
                <Div>
                  <Gutter size="lg" />
                  <Divider width={90} />
                  <Gutter size="lg" />
                </Div>
              </Div>
            ))}
          </Div>
        </GridBag.Item>
        {/* Right-side container */}
        <GridBag.Item md={3}>
          <Div>
            <Div alignTextToRight padding={{ top: 'sm' }}>
              <Div inlineBlock={true} padding={{ left: 'md', top: 'sm' }}>
                <Button
                  id={saveProductButtonId}
                  onClick={async () => {
                    await ensureEditableItemInputChangesSavedOnSaveBtnClick(saveProductButtonId);
                    setSaveProductChanges(true);
                  }}
                >
                  {t('editscreen.buttons.save_changes')}
                </Button>
              </Div>
            </Div>
            <Gutter size="xlg" />
            <Div>
              <Accordion heading={t('editscreen.translation')} initiallyExpanded={true} showOverflow={true}>
                <Dropdown
                  name="currentLocale"
                  label={t('editscreen.translation')}
                  onChange={(_, option) => {
                    handleLocaleChange(Number(option.value));
                  }}
                  selectedOption={{ label: currentLocale.name, value: currentLocale.id.toString() }}
                  options={locales.map(locale => ({ label: locale.name, value: locale.id.toString() }))}
                />
                <Gutter />
                <Dropdown
                  label={t('editscreen.reference')}
                  onChange={() => undefined}
                  selectedOption={{ label: t('editscreen.reference_english'), value: '' }}
                  options={[]}
                  disabled={true}
                />
              </Accordion>
              <Gutter />
              <Sections sectionTitles={sectionTitles} sectionsRef={sectionsRef} scrollOnHash />
            </Div>
          </Div>
        </GridBag.Item>
      </GridBag>
    </Div>
  );
}
